import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Plus, Trash2, GripVertical, Settings2, ChevronDown, ChevronUp, Pencil } from 'lucide-react';
import { ConditionalOperator } from './conditional-field.types';
import type { ShowWhenConfig } from './conditional-field.types';

export { ConditionalOperator };
export type { ShowWhenConfig };
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardHeader,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
} from '@/components/ui/collapsible';

export interface CustomFieldValidation {
  type: 'min' | 'max' | 'minLength' | 'maxLength' | 'regex' | 'minAge' | 'maxAge';
  value: any;
  errorMessage: string;
}

export interface RepeatGroupConfig {
  groupKey: string;
  groupLabel: string;
  maxRepetitions?: number;
  fields: CustomFieldConfig[];
}

export type ConditionalMessageType = 'info' | 'warning' | 'error';

export interface ConditionalMessageConfig {
  message: string;
  type: ConditionalMessageType;
  showWhen: ShowWhenConfig;
}

export interface CustomFieldConfig {
  fieldKey: string;
  fieldType: 'text' | 'number' | 'date' | 'select' | 'checkbox' | 'textarea';
  label: string;
  placeholder?: string;
  description?: string;
  required: boolean;
  order: number;
  validations?: CustomFieldValidation[];
  options?: string[];
  defaultValue?: any;
  showWhen?: ShowWhenConfig;
  repeatGroup?: RepeatGroupConfig;
  conditionalMessage?: ConditionalMessageConfig;
  showInOperatorList?: boolean;
}

interface CustomFieldsBuilderProps {
  fields: CustomFieldConfig[];
  onChange: (fields: CustomFieldConfig[]) => void;
  maxFields?: number;
  isSubBuilder?: boolean;
}

const FIELD_TYPE_OPTIONS = [
  { value: 'text', label: 'Texto' },
  { value: 'number', label: 'Número' },
  { value: 'date', label: 'Data' },
  { value: 'select', label: 'Seleção' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'textarea', label: 'Área de Texto' },
];

const VALIDATION_TYPES_BY_FIELD = {
  text: [
    { value: 'minLength', label: 'Comprimento Mínimo' },
    { value: 'maxLength', label: 'Comprimento Máximo' },
    { value: 'regex', label: 'Padrão (Regex)' },
  ],
  textarea: [
    { value: 'minLength', label: 'Comprimento Mínimo' },
    { value: 'maxLength', label: 'Comprimento Máximo' },
  ],
  number: [
    { value: 'min', label: 'Valor Mínimo' },
    { value: 'max', label: 'Valor Máximo' },
  ],
  date: [
    { value: 'minAge', label: 'Idade Mínima' },
    { value: 'maxAge', label: 'Idade Máxima' },
  ],
  select: [],
  checkbox: [],
};

export const CustomFieldsBuilder: React.FC<CustomFieldsBuilderProps> = ({
  fields,
  onChange,
  maxFields = 20,
  isSubBuilder = false,
}) => {
  const [editingField, setEditingField] = useState<CustomFieldConfig | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  const [fieldBeingEdited, setFieldBeingEdited] = useState<CustomFieldConfig | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(fields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order property
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index,
    }));

    onChange(updatedItems);
  };

  const addField = (field: CustomFieldConfig) => {
    const newField = {
      ...field,
      order: fields.length,
    };
    onChange([...fields, newField]);
    setIsAddDialogOpen(false);
    setEditingField(null);
  };

  const updateField = (fieldKey: string, updates: Partial<CustomFieldConfig>) => {
    const updatedFields = fields.map((field) =>
      field.fieldKey === fieldKey ? { ...field, ...updates } : field
    );
    onChange(updatedFields);
  };

  const deleteField = (fieldKey: string) => {
    const filteredFields = fields.filter((field) => field.fieldKey !== fieldKey);
    // Re-order remaining fields
    const reorderedFields = filteredFields.map((field, index) => ({
      ...field,
      order: index,
    }));
    onChange(reorderedFields);
  };

  const toggleFieldExpansion = (fieldKey: string) => {
    const newExpanded = new Set(expandedFields);
    if (newExpanded.has(fieldKey)) {
      newExpanded.delete(fieldKey);
    } else {
      newExpanded.add(fieldKey);
    }
    setExpandedFields(newExpanded);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-[16px] font-semibold">Campos Personalizados</h3>
          <p className="text-sm text-muted-foreground">
            Configure campos adicionais para coleta de dados ({fields.length}/{maxFields})
          </p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={fields.length >= maxFields}
              onClick={() => {
                setEditingField({
                  fieldKey: '',
                  fieldType: 'text',
                  label: '',
                  placeholder: '',
                  description: '',
                  required: false,
                  order: fields.length,
                  validations: [],
                  options: [],
                });
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Campo
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Adicionar Campo Personalizado</DialogTitle>
              <DialogDescription>
                Configure um novo campo para coleta de dados dos participantes
              </DialogDescription>
            </DialogHeader>
            {editingField && (
              <FieldEditor
                field={editingField}
                allFields={fields}
                onSave={addField}
                onCancel={() => {
                  setIsAddDialogOpen(false);
                  setEditingField(null);
                }}
                isSubField={isSubBuilder}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Campo Personalizado</DialogTitle>
            <DialogDescription>Modifique as configurações do campo</DialogDescription>
          </DialogHeader>
          {fieldBeingEdited && (
            <FieldEditor
              field={fieldBeingEdited}
              allFields={fields.filter((f) => f.fieldKey !== fieldBeingEdited.fieldKey)}
              onSave={(updated) => {
                const updatedFields = fields.map((f) =>
                  f.fieldKey === fieldBeingEdited.fieldKey ? updated : f
                );
                onChange(updatedFields);
                setIsEditDialogOpen(false);
                setFieldBeingEdited(null);
              }}
              onCancel={() => {
                setIsEditDialogOpen(false);
                setFieldBeingEdited(null);
              }}
              isSubField={isSubBuilder}
            />
          )}
        </DialogContent>
      </Dialog>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="fields">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
              {fields.map((field, index) => (
                <Draggable key={field.fieldKey} draggableId={field.fieldKey} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`${
                        snapshot.isDragging ? 'shadow-lg' : ''
                      } transition-shadow`}
                    >
                      <Card>
                        <CardHeader className="p-3">
                          <div className="flex items-center gap-2">
                            <div
                              {...provided.dragHandleProps}
                              className="cursor-move text-muted-foreground hover:text-muted-foreground"
                            >
                              <GripVertical className="w-5 h-5" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{field.label}</span>
                                  <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                                    {FIELD_TYPE_OPTIONS.find((t) => t.value === field.fieldType)?.label}
                                  </span>
                                  {field.required && (
                                    <span className="text-xs text-status-danger">*Obrigatório</span>
                                  )}
                                  {field.showWhen && (
                                    <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                                      Condicional
                                    </span>
                                  )}
                                  {field.repeatGroup && (
                                    <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded">
                                      Grupo ({field.repeatGroup.fields.length} campos)
                                    </span>
                                  )}
                                  {field.conditionalMessage && (
                                    <span className={`text-xs px-2 py-1 rounded ${
                                      field.conditionalMessage.type === 'info'
                                        ? 'text-primary bg-primary/10'
                                        : field.conditionalMessage.type === 'warning'
                                        ? 'text-status-attention bg-brand-attention/10'
                                        : 'text-status-danger bg-brand-danger/10'
                                    }`}>
                                      Tem mensagem
                                    </span>
                                  )}
                                  {field.showInOperatorList && (
                                    <span className="text-xs text-status-ok bg-brand-green/10 px-2 py-1 rounded">
                                      Operador
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleFieldExpansion(field.fieldKey)}
                                  >
                                    {expandedFields.has(field.fieldKey) ? (
                                      <ChevronUp className="w-4 h-4" />
                                    ) : (
                                      <ChevronDown className="w-4 h-4" />
                                    )}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setFieldBeingEdited(field);
                                      setIsEditDialogOpen(true);
                                    }}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteField(field.fieldKey)}
                                  >
                                    <Trash2 className="w-4 h-4 text-status-danger" />
                                  </Button>
                                </div>
                              </div>
                              {field.description && (
                                <p className="text-sm text-muted-foreground mt-1">{field.description}</p>
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        <Collapsible open={expandedFields.has(field.fieldKey)}>
                          <CollapsibleContent>
                            <CardContent className="p-3 pt-0">
                              <FieldQuickEdit
                                field={field}
                                onUpdate={(updates) => updateField(field.fieldKey, updates)}
                              />
                            </CardContent>
                          </CollapsibleContent>
                        </Collapsible>
                      </Card>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {fields.length === 0 && (
        <Card className="p-8 text-center text-muted-foreground">
          <Settings2 className="w-12 h-12 mx-auto mb-4 text-foreground" />
          <p className="text-lg font-medium mb-2">Nenhum campo personalizado</p>
          <p className="text-sm">
            Adicione campos personalizados para coletar informações específicas dos participantes
          </p>
        </Card>
      )}
    </div>
  );
};

// Component for quick inline editing
const FieldQuickEdit: React.FC<{
  field: CustomFieldConfig;
  onUpdate: (updates: Partial<CustomFieldConfig>) => void;
}> = ({ field, onUpdate }) => {
  const validationTypes = VALIDATION_TYPES_BY_FIELD[field.fieldType] || [];
  const [newOption, setNewOption] = useState('');

  const addOption = () => {
    if (newOption.trim()) {
      onUpdate({
        options: [...(field.options || []), newOption.trim()],
      });
      setNewOption('');
    }
  };

  const removeOption = (index: number) => {
    const newOptions = [...(field.options || [])];
    newOptions.splice(index, 1);
    onUpdate({ options: newOptions });
  };

  const addValidation = (type: string) => {
    const newValidation: CustomFieldValidation = {
      type: type as any,
      value: '',
      errorMessage: '',
    };
    onUpdate({
      validations: [...(field.validations || []), newValidation],
    });
  };

  const updateValidation = (index: number, updates: Partial<CustomFieldValidation>) => {
    const newValidations = [...(field.validations || [])];
    newValidations[index] = { ...newValidations[index], ...updates };
    onUpdate({ validations: newValidations });
  };

  const removeValidation = (index: number) => {
    const newValidations = [...(field.validations || [])];
    newValidations.splice(index, 1);
    onUpdate({ validations: newValidations });
  };

  return (
    <div className="space-y-4 text-sm">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Chave do Campo</Label>
          <Input
            value={field.fieldKey}
            onChange={(e) => onUpdate({ fieldKey: e.target.value })}
            placeholder="ex: cpf, idade"
            pattern="^[a-zA-Z][a-zA-Z0-9_]*$"
          />
        </div>
        <div>
          <Label>Texto de Ajuda</Label>
          <Input
            value={field.placeholder}
            onChange={(e) => onUpdate({ placeholder: e.target.value })}
            placeholder="Ex: Digite seu CPF"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          checked={field.required}
          onCheckedChange={(checked) => onUpdate({ required: checked })}
        />
        <Label>Campo Obrigatório</Label>
      </div>

      {field.fieldType === 'select' && (
        <div>
          <Label>Opções de Seleção</Label>
          <div className="space-y-2 mt-2">
            {field.options?.map((option, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input value={option} disabled />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeOption(index)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <Input
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                placeholder="Nova opção"
                onKeyDown={(e) => e.key === 'Enter' && addOption()}
              />
              <Button size="sm" onClick={addOption}>
                Adicionar
              </Button>
            </div>
          </div>
        </div>
      )}

      {validationTypes.length > 0 && (
        <div>
          <Label>Validações</Label>
          <div className="space-y-2 mt-2">
            {field.validations?.map((validation, index) => (
              <div key={index} className="flex items-center gap-2">
                <Select
                  value={validation.type}
                  onValueChange={(value) =>
                    updateValidation(index, { type: value as any })
                  }
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {validationTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  className="w-24"
                  value={validation.value}
                  onChange={(e) =>
                    updateValidation(index, { value: e.target.value })
                  }
                  placeholder="Valor"
                />
                <Input
                  className="flex-1"
                  value={validation.errorMessage}
                  onChange={(e) =>
                    updateValidation(index, { errorMessage: e.target.value })
                  }
                  placeholder="Mensagem de erro"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeValidation(index)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Select
              value=""
              onValueChange={(value) => addValidation(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Adicionar validação" />
              </SelectTrigger>
              <SelectContent>
                {validationTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
};

const OPERATOR_LABELS: Record<ConditionalOperator, string> = {
  [ConditionalOperator.EQUALS]: 'É igual a',
  [ConditionalOperator.NOT_EQUALS]: 'É diferente de',
  [ConditionalOperator.GREATER_THAN]: 'É maior que',
  [ConditionalOperator.LESS_THAN]: 'É menor que',
  [ConditionalOperator.GREATER_THAN_OR_EQUAL]: 'É maior ou igual a',
  [ConditionalOperator.HAS_VALUE]: 'Tem algum valor',
  [ConditionalOperator.IS_EMPTY]: 'Está vazio',
};

const OPERATORS_WITH_VALUE = [
  ConditionalOperator.EQUALS,
  ConditionalOperator.NOT_EQUALS,
  ConditionalOperator.GREATER_THAN,
  ConditionalOperator.LESS_THAN,
  ConditionalOperator.GREATER_THAN_OR_EQUAL,
];

// Full field editor dialog component
const FieldEditor: React.FC<{
  field: CustomFieldConfig;
  allFields: CustomFieldConfig[];
  onSave: (field: CustomFieldConfig) => void;
  onCancel: () => void;
  isSubField?: boolean;
}> = ({ field, allFields, onSave, onCancel, isSubField = false }) => {
  const existingKeys = allFields.map((f) => f.fieldKey);
  const otherFields = allFields.filter((f) => f.fieldKey !== field.fieldKey);

  const [editedField, setEditedField] = useState(field);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showWhenEnabled, setShowWhenEnabled] = useState(!!field.showWhen);
  const [repeatGroupEnabled, setRepeatGroupEnabled] = useState(!!field.repeatGroup);
  const [conditionalMessageEnabled, setConditionalMessageEnabled] = useState(!!field.conditionalMessage);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!editedField.fieldKey) {
      newErrors.fieldKey = 'Chave do campo é obrigatória';
    } else if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(editedField.fieldKey)) {
      newErrors.fieldKey = 'Chave deve começar com letra e conter apenas letras, números e underscore';
    } else if (existingKeys.includes(editedField.fieldKey) && field.fieldKey !== editedField.fieldKey) {
      newErrors.fieldKey = 'Esta chave já está em uso';
    }

    if (!editedField.label) {
      newErrors.label = 'Rótulo é obrigatório';
    }

    if (editedField.fieldType === 'select' && (!editedField.options || editedField.options.length === 0 || editedField.options.every(opt => !opt.trim()))) {
      newErrors.options = 'Campos de seleção precisam ter pelo menos uma opção válida';
    }

    if (showWhenEnabled && !editedField.showWhen?.fieldKey) {
      newErrors.showWhenFieldKey = 'Selecione o campo gatilho';
    }

    if (repeatGroupEnabled) {
      if (!editedField.repeatGroup?.groupKey) {
        newErrors.groupKey = 'Chave do grupo é obrigatória';
      } else if (!/^[a-zA-Z][a-zA-Z0-9_]{1,29}$/.test(editedField.repeatGroup.groupKey)) {
        newErrors.groupKey = 'Chave deve começar com letra, conter letras/números/underscore, ter 2-30 caracteres';
      } else if (existingKeys.includes(editedField.repeatGroup.groupKey)) {
        newErrors.groupKey = 'Esta chave já está em uso por outro campo';
      }

      if (!editedField.repeatGroup?.groupLabel) {
        newErrors.groupLabel = 'Nome de cada item é obrigatório';
      }
    }

    if (conditionalMessageEnabled) {
      if (!editedField.conditionalMessage?.message?.trim()) {
        newErrors.conditionalMessageText = 'A mensagem não pode ser vazia';
      }
      if (!editedField.conditionalMessage?.showWhen?.fieldKey) {
        newErrors.conditionalMessageFieldKey = 'Selecione o campo gatilho da mensagem';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    // Filter out empty options before saving
    const fieldToSave = {
      ...editedField,
      options: editedField.fieldType === 'select'
        ? editedField.options?.filter(opt => opt.trim() !== '')
        : editedField.options
    };

    if (validate()) {
      onSave(fieldToSave);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Chave do Campo*</Label>
        <Input
          value={editedField.fieldKey}
          onChange={(e) => setEditedField({ ...editedField, fieldKey: e.target.value })}
          placeholder="ex: cpf, data_nascimento"
        />
        {errors.fieldKey && <p className="text-status-danger text-sm mt-1">{errors.fieldKey}</p>}
      </div>

      <div>
        <Label>Rótulo*</Label>
        <Input
          value={editedField.label}
          onChange={(e) => setEditedField({ ...editedField, label: e.target.value })}
          placeholder="Ex: CPF, Data de Nascimento"
        />
        {errors.label && <p className="text-status-danger text-sm mt-1">{errors.label}</p>}
      </div>

      <div>
        <Label>Tipo de Campo</Label>
        <Select
          value={editedField.fieldType}
          onValueChange={(value: any) => setEditedField({ ...editedField, fieldType: value })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FIELD_TYPE_OPTIONS.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Placeholder</Label>
        <Input
          value={editedField.placeholder}
          onChange={(e) => setEditedField({ ...editedField, placeholder: e.target.value })}
          placeholder="Texto de ajuda dentro do campo"
        />
      </div>

      <div>
        <Label>Descrição</Label>
        <Textarea
          value={editedField.description}
          onChange={(e) => setEditedField({ ...editedField, description: e.target.value })}
          placeholder="Texto explicativo sobre o campo"
          rows={2}
        />
      </div>

      <div className="flex items-center gap-2">
        <Switch
          checked={editedField.required}
          onCheckedChange={(checked) => setEditedField({ ...editedField, required: checked })}
        />
        <Label>Campo Obrigatório</Label>
      </div>

      {!isSubField && (
        <div className="flex items-center gap-2">
          <Switch
            checked={editedField.showInOperatorList ?? false}
            onCheckedChange={(checked) => setEditedField({ ...editedField, showInOperatorList: checked })}
          />
          <Label>Mostrar na listagem do operador</Label>
        </div>
      )}

      {editedField.fieldType === 'select' && (
        <div>
          <Label>Opções de Seleção*</Label>
          <div className="space-y-2 mt-2">
            {editedField.options?.map((option, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={option}
                  onChange={(e) => {
                    const newOptions = [...(editedField.options || [])];
                    newOptions[index] = e.target.value;
                    setEditedField({ ...editedField, options: newOptions });
                  }}
                  placeholder="Opção"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const newOptions = [...(editedField.options || [])];
                    newOptions.splice(index, 1);
                    setEditedField({ ...editedField, options: newOptions });
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setEditedField({
                  ...editedField,
                  options: [...(editedField.options || []), '']
                });
              }}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Opção
            </Button>
            {errors.options && <p className="text-status-danger text-sm">{errors.options}</p>}
          </div>
        </div>
      )}

      <div className="border rounded-ds-card p-4 space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground">Visibilidade Condicional</h4>
          <div className="flex items-center gap-2">
            <Switch
              checked={showWhenEnabled}
              onCheckedChange={(checked) => {
                setShowWhenEnabled(checked);
                if (!checked) {
                  setEditedField((prev) => {
                    const { showWhen: _sw, ...rest } = prev;
                    return rest as CustomFieldConfig;
                  });
                }
              }}
            />
            <Label>Mostrar apenas quando...</Label>
          </div>

          {showWhenEnabled && (
            <div className="space-y-3 pl-2">
              <div>
                <Label>Campo gatilho</Label>
                <Select
                  value={editedField.showWhen?.fieldKey ?? ''}
                  onValueChange={(value) =>
                    setEditedField({
                      ...editedField,
                      showWhen: {
                        fieldKey: value,
                        operator: editedField.showWhen?.operator ?? ConditionalOperator.HAS_VALUE,
                        value: editedField.showWhen?.value,
                      },
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um campo..." />
                  </SelectTrigger>
                  <SelectContent>
                    {otherFields.map((f) => (
                      <SelectItem key={f.fieldKey} value={f.fieldKey}>
                        {f.label} ({f.fieldKey})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.showWhenFieldKey && (
                  <p className="text-status-danger text-sm mt-1">{errors.showWhenFieldKey}</p>
                )}
              </div>

              <div>
                <Label>Operador</Label>
                <Select
                  value={editedField.showWhen?.operator ?? ConditionalOperator.HAS_VALUE}
                  onValueChange={(value) =>
                    setEditedField({
                      ...editedField,
                      showWhen: {
                        fieldKey: editedField.showWhen?.fieldKey ?? '',
                        operator: value as ConditionalOperator,
                        value: editedField.showWhen?.value,
                      },
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(ConditionalOperator).map((op) => (
                      <SelectItem key={op} value={op}>
                        {OPERATOR_LABELS[op]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {OPERATORS_WITH_VALUE.includes(
                (editedField.showWhen?.operator ?? ConditionalOperator.HAS_VALUE) as ConditionalOperator
              ) && (
                <div>
                  <Label>Valor</Label>
                  <Input
                    value={editedField.showWhen?.value ?? ''}
                    onChange={(e) =>
                      setEditedField({
                        ...editedField,
                        showWhen: {
                          fieldKey: editedField.showWhen?.fieldKey ?? '',
                          operator: editedField.showWhen?.operator ?? ConditionalOperator.HAS_VALUE,
                          value: e.target.value,
                        },
                      })
                    }
                    placeholder="Valor para comparação"
                  />
                </div>
              )}
            </div>
          )}
        </div>

      {editedField.fieldType === 'number' && !isSubField && (
        <div className="border rounded-ds-card p-4 space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground">Grupo Repetível</h4>
          <div className="flex items-center gap-2">
            <Switch
              checked={repeatGroupEnabled}
              onCheckedChange={(checked) => {
                setRepeatGroupEnabled(checked);
                if (checked) {
                  setEditedField((prev) => ({
                    ...prev,
                    repeatGroup: prev.repeatGroup ?? {
                      groupKey: '',
                      groupLabel: '',
                      maxRepetitions: 20,
                      fields: [],
                    },
                  } as CustomFieldConfig));
                } else {
                  setEditedField((prev) => {
                    const { repeatGroup: _rg, ...rest } = prev;
                    return rest as CustomFieldConfig;
                  });
                }
              }}
            />
            <Label>Este campo controla um grupo repetível</Label>
          </div>

          {repeatGroupEnabled && (
            <div className="space-y-4 pl-2">
              <div>
                <Label>Chave do grupo*</Label>
                <Input
                  value={editedField.repeatGroup?.groupKey ?? ''}
                  onChange={(e) =>
                    setEditedField((prev) => ({
                      ...prev,
                      repeatGroup: {
                        ...(prev.repeatGroup ?? { groupKey: '', groupLabel: '', fields: [] }),
                        groupKey: e.target.value,
                      },
                    }))
                  }
                  placeholder="ex: menores, veiculos"
                />
                {errors.groupKey && <p className="text-status-danger text-sm mt-1">{errors.groupKey}</p>}
              </div>

              <div>
                <Label>Nome de cada item*</Label>
                <Input
                  value={editedField.repeatGroup?.groupLabel ?? ''}
                  onChange={(e) =>
                    setEditedField((prev) => ({
                      ...prev,
                      repeatGroup: {
                        ...(prev.repeatGroup ?? { groupKey: '', groupLabel: '', fields: [] }),
                        groupLabel: e.target.value,
                      },
                    }))
                  }
                  placeholder="ex: Menor, Veículo"
                />
                {errors.groupLabel && <p className="text-status-danger text-sm mt-1">{errors.groupLabel}</p>}
              </div>

              <div>
                <Label>Máximo de repetições</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={editedField.repeatGroup?.maxRepetitions ?? 20}
                  onChange={(e) =>
                    setEditedField((prev) => ({
                      ...prev,
                      repeatGroup: {
                        ...(prev.repeatGroup ?? { groupKey: '', groupLabel: '', fields: [] }),
                        maxRepetitions: Math.min(20, Math.max(1, parseInt(e.target.value) || 1)),
                      },
                    }))
                  }
                />
              </div>

              <div>
                <Label>Campos do grupo</Label>
                <div className="mt-2 border rounded p-3">
                  <CustomFieldsBuilder
                    fields={editedField.repeatGroup?.fields ?? []}
                    onChange={(subFields) =>
                      setEditedField((prev) => ({
                        ...prev,
                        repeatGroup: {
                          ...(prev.repeatGroup ?? { groupKey: '', groupLabel: '', fields: [] }),
                          fields: subFields,
                        },
                      }))
                    }
                    maxFields={10}
                    isSubBuilder={true}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="border rounded-ds-card p-4 space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground">Mensagem Condicional</h4>
        <div className="flex items-center gap-2">
          <Switch
            checked={conditionalMessageEnabled}
            onCheckedChange={(checked) => {
              setConditionalMessageEnabled(checked);
              if (checked) {
                setEditedField((prev) => ({
                  ...prev,
                  conditionalMessage: prev.conditionalMessage ?? {
                    message: '',
                    type: 'info' as ConditionalMessageType,
                    showWhen: {
                      fieldKey: '',
                      operator: ConditionalOperator.HAS_VALUE,
                    },
                  },
                }));
              } else {
                setEditedField((prev) => {
                  const { conditionalMessage: _cm, ...rest } = prev;
                  return rest as CustomFieldConfig;
                });
              }
            }}
          />
          <Label>Exibir mensagem quando...</Label>
        </div>

        {conditionalMessageEnabled && (
          <div className="space-y-3 pl-2">
            <div>
              <Label>Tipo</Label>
              <Select
                value={editedField.conditionalMessage?.type ?? 'info'}
                onValueChange={(value) =>
                  setEditedField((prev) => ({
                    ...prev,
                    conditionalMessage: {
                      ...(prev.conditionalMessage ?? { message: '', type: 'info' as ConditionalMessageType, showWhen: { fieldKey: '', operator: ConditionalOperator.HAS_VALUE } }),
                      type: value as ConditionalMessageType,
                    },
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Informação</SelectItem>
                  <SelectItem value="warning">Aviso</SelectItem>
                  <SelectItem value="error">Erro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Mensagem</Label>
              <Textarea
                value={editedField.conditionalMessage?.message ?? ''}
                onChange={(e) =>
                  setEditedField((prev) => ({
                    ...prev,
                    conditionalMessage: {
                      ...(prev.conditionalMessage ?? { message: '', type: 'info' as ConditionalMessageType, showWhen: { fieldKey: '', operator: ConditionalOperator.HAS_VALUE } }),
                      message: e.target.value,
                    },
                  }))
                }
                placeholder="Ex: Menores precisam de responsável presente"
                rows={2}
              />
              {errors.conditionalMessageText && (
                <p className="text-status-danger text-sm mt-1">{errors.conditionalMessageText}</p>
              )}
            </div>

            <div>
              <Label>Campo gatilho</Label>
              <Select
                value={editedField.conditionalMessage?.showWhen?.fieldKey ?? ''}
                onValueChange={(value) =>
                  setEditedField((prev) => ({
                    ...prev,
                    conditionalMessage: {
                      ...(prev.conditionalMessage ?? { message: '', type: 'info' as ConditionalMessageType, showWhen: { fieldKey: '', operator: ConditionalOperator.HAS_VALUE } }),
                      showWhen: {
                        fieldKey: value,
                        operator: prev.conditionalMessage?.showWhen?.operator ?? ConditionalOperator.HAS_VALUE,
                        value: prev.conditionalMessage?.showWhen?.value,
                      },
                    },
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um campo..." />
                </SelectTrigger>
                <SelectContent>
                  {otherFields.map((f) => (
                    <SelectItem key={f.fieldKey} value={f.fieldKey}>
                      {f.label} ({f.fieldKey})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.conditionalMessageFieldKey && (
                <p className="text-status-danger text-sm mt-1">{errors.conditionalMessageFieldKey}</p>
              )}
            </div>

            <div>
              <Label>Operador</Label>
              <Select
                value={editedField.conditionalMessage?.showWhen?.operator ?? ConditionalOperator.HAS_VALUE}
                onValueChange={(value) =>
                  setEditedField((prev) => ({
                    ...prev,
                    conditionalMessage: {
                      ...(prev.conditionalMessage ?? { message: '', type: 'info' as ConditionalMessageType, showWhen: { fieldKey: '', operator: ConditionalOperator.HAS_VALUE } }),
                      showWhen: {
                        fieldKey: prev.conditionalMessage?.showWhen?.fieldKey ?? '',
                        operator: value as ConditionalOperator,
                        value: prev.conditionalMessage?.showWhen?.value,
                      },
                    },
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(ConditionalOperator).map((op) => (
                    <SelectItem key={op} value={op}>
                      {OPERATOR_LABELS[op]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {OPERATORS_WITH_VALUE.includes(
              (editedField.conditionalMessage?.showWhen?.operator ?? ConditionalOperator.HAS_VALUE) as ConditionalOperator
            ) && (
              <div>
                <Label>Valor</Label>
                <Input
                  value={editedField.conditionalMessage?.showWhen?.value ?? ''}
                  onChange={(e) =>
                    setEditedField((prev) => ({
                      ...prev,
                      conditionalMessage: {
                        ...(prev.conditionalMessage ?? { message: '', type: 'info' as ConditionalMessageType, showWhen: { fieldKey: '', operator: ConditionalOperator.HAS_VALUE } }),
                        showWhen: {
                          fieldKey: prev.conditionalMessage?.showWhen?.fieldKey ?? '',
                          operator: prev.conditionalMessage?.showWhen?.operator ?? ConditionalOperator.HAS_VALUE,
                          value: e.target.value,
                        },
                      },
                    }))
                  }
                  placeholder="Valor para comparação"
                />
              </div>
            )}
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button onClick={handleSave}>
          Salvar Campo
        </Button>
      </DialogFooter>
    </div>
  );
};